import { useState, type ReactNode } from "react";
import classNames from "classnames";
import type { MaterialNames } from "@/components/material_symbol_names";

export const GLOBAL_SEARCH_GLYPH_STYLE = {
  fontFamily: "Material Symbols Rounded",
  fontVariationSettings: "'FILL' 0",
  fontWeight: 400
} as const;

export type GlobalSearchRowProps = {
  className?: string;
  iconName?: MaterialNames;
  picture?: string;
  headline: ReactNode;
  supportingText?: ReactNode;
  trailingSupportingText?: string | null | undefined;
  /** Optional trailing control (for example a recents remove button). Keep it Lit-free. */
  trailingAction?: ReactNode;
};

function LeadingGlyph({ iconName }: { iconName: MaterialNames }) {
  return (
    <div
      role="img"
      aria-label="Profile photo"
      className="object-cover rounded-full w-10 h-10 bg-sys-brand-secondary-container flex items-center justify-center"
      data-testid="avatar-person-icon"
    >
      <span
        className="material-symbols-rounded text-[24px] leading-none text-sys-brand-on-surface-variant"
        data-testid={`icon-${iconName}`}
        style={GLOBAL_SEARCH_GLYPH_STYLE}
      >
        {iconName}
      </span>
    </div>
  );
}

function LeadingPicture({ picture, iconName }: { picture: string; iconName: MaterialNames }) {
  const [failedPicture, setFailedPicture] = useState<string | undefined>();
  const showImage = picture !== failedPicture;

  if (!showImage) return <LeadingGlyph iconName={iconName} />;

  return (
    <img
      src={picture}
      alt="Rounded avatar"
      className="object-cover rounded-full w-10 h-10"
      data-testid="avatar-image"
      onError={() => setFailedPicture(picture)}
    />
  );
}

export default function GlobalSearchRow({
  className,
  iconName = "history",
  picture,
  headline,
  supportingText,
  trailingSupportingText,
  trailingAction
}: GlobalSearchRowProps) {
  const hasTrailing = Boolean(trailingSupportingText) || trailingAction != null;
  return (
    <div className={classNames("flex justify-between w-full min-w-0 items-center my-0 text-left", className)}>
      <div className="items-center flex space-x-2 w-full min-w-0">
        {picture ? <LeadingPicture picture={picture} iconName={iconName} /> : <LeadingGlyph iconName={iconName} />}
        <div className="flex flex-col items-start min-w-0 flex-1 gap-0">
          <div className="break-words text-sys-brand-on-surface w-full min-w-0 text-body-large-semibold">
            {headline || "--"}
          </div>
          {supportingText ? (
            <div className="text-sys-brand-on-surface-variant break-words w-full min-w-0 text-body-medium">
              {supportingText}
            </div>
          ) : null}
        </div>
      </div>
      {hasTrailing ? (
        <div className="flex shrink-0 items-center gap-1 pl-2">
          {trailingSupportingText ? (
            <div className="break-words text-sys-brand-on-surface text-label-medium">{trailingSupportingText}</div>
          ) : null}
          {trailingAction}
        </div>
      ) : null}
    </div>
  );
}
